import React from 'react';
import {Chip, Tooltip} from '@mui/material';
import {useTranslation} from 'react-i18next';

/**
 * Cosine-similarity thresholds for the semantic search. Chosen empirically:
 *   ≥ 0.75 is a near-perfect semantic match,
 *   ≥ 0.60 is clearly related,
 *   ≥ 0.45 is loosely related (may be noise),
 *   below that is essentially a miss.
 */
const STRONG = 0.75;
const GOOD = 0.60;
const WEAK = 0.45;

type ScoreTier = 'strong' | 'good' | 'weak' | 'poor';

const classify = (score: number): ScoreTier => {
    if (score >= STRONG) return 'strong';
    if (score >= GOOD) return 'good';
    if (score >= WEAK) return 'weak';
    return 'poor';
};

const tierColor: Record<ScoreTier, 'success' | 'info' | 'warning' | 'default'> = {
    strong: 'success',
    good: 'info',
    weak: 'warning',
    poor: 'default',
};

interface SearchScoreChipProps {
    score: number;
    size?: 'small' | 'medium';
    /** Render a tiny dot-only variant for dense table cells. */
    dense?: boolean;
}

/**
 * Compact visualization of a semantic-search cosine-similarity score.
 * Shows percentage with a color-coded tier and a tooltip explaining the label.
 */
export const SearchScoreChip: React.FC<SearchScoreChipProps> = ({score, size = 'small', dense}) => {
    const {t} = useTranslation();
    const tier = classify(score);
    const percent = Math.round(score * 100);
    const label = t(`common.searchScore.tier.${tier}`);

    return (
        <Tooltip title={`${label} · ${(score * 100).toFixed(1)} %`}>
            <Chip
                size={size}
                color={tierColor[tier]}
                variant={tier === 'poor' ? 'outlined' : 'filled'}
                label={dense ? `${percent}%` : `${percent} %`}
                sx={{
                    fontWeight: 600,
                    fontVariantNumeric: 'tabular-nums',
                    minWidth: dense ? 48 : undefined,
                }}
            />
        </Tooltip>
    );
};
